import React, { useEffect, useState } from "react";
import { Container } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import { getBlogDetailApi } from "../../Service/blog";
import { BLOG, BLOG_DETAIL } from "../../Routes/RouteConstent";
import { useBlogPageMeta } from "./useBlogPageMeta";
import BlogStayInLoopCTA from "./BlogStayInLoopCTA";
import BlogDetailShare from "./BlogDetailShare";
import { getBlogCardImage, getBlogDetailCoverImage } from "./blogImageUtils";
import BlogLoading from "./BlogLoading";
import "./Blog.scss";

const BlogDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const [blog, setBlog] = useState<any>(null);
  const [features, setFeatures] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (!slug) return;
      setLoading(true);
      try {
        const res: any = await getBlogDetailApi(slug);
        if (res?.data?.success) {
          setBlog(res.data.blog);
          setFeatures(res.data.features || []);
        } else {
          setBlog(null);
        }
      } catch (e) {
        setBlog(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug]);

  useBlogPageMeta({
    title: blog?.meta_key || blog?.title || "Blog | Nurture by NAPS™",
    description:
      blog?.meta_description ||
      blog?.summary ||
      "Parenting guidance from Nurture by NAPS™.",
    canonicalPath: slug ? `/blog/${slug}` : BLOG,
    image: blog ? getBlogDetailCoverImage(blog) : undefined,
  });

  if (loading) {
    return (
      <div className="blog-page blog-detail-page">
        <BlogLoading label="Loading article…" />
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="blog-page py-5 text-center">
        <p>Blog post not found.</p>
        <Link to={BLOG}>Back to Blog</Link>
      </div>
    );
  }

  const coverImage = getBlogDetailCoverImage(blog);
  const shareUrl = typeof window !== "undefined" ? window.location.href : blog.url;

  return (
    <div className="blog-page blog-detail-page">
      <Container className="blog-detail-container blog-detail-cover-wrap pt-3 pt-md-4">
        <div className="row justify-content-center">
          <div className="col-12">
            {coverImage ? (
              <figure className="blog-detail-cover">
                <img src={coverImage} alt="" decoding="async" />
              </figure>
            ) : null}
          </div>
        </div>
      </Container>

      <section className="blog-detail-section">
        <Container className="blog-detail-container py-4 py-md-5">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <Link to={BLOG} className="back-link d-inline-block mb-3">
                ← Back to Blog
              </Link>
              <h1 className="section-title mb-4">{blog.title}</h1>

              <div className="blog-detail-meta mb-4">
                <div className="blog-detail-meta__main">
                  <div className="blog-detail-meta__author">
                    {blog.user?.photo ? (
                      <img src={blog.user.photo} alt="" className="author-avatar" />
                    ) : null}
                    <div>
                      {blog.user?.name ? (
                        <div className="blog-detail-meta__author-name">{blog.user.name}</div>
                      ) : null}
                      <time className="blog-detail-meta__date" dateTime={blog.date}>
                        {blog.date || blog.days}
                      </time>
                    </div>
                  </div>

                  {Array.isArray(blog.categories) && blog.categories.length > 0 ? (
                    <div className="blog-detail-meta__categories">
                      {blog.categories.map((cat: any) => (
                        <span className="tag" key={cat.id}>
                          {cat.name}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>

                <BlogDetailShare
                  shareUrl={shareUrl}
                  title={blog.title}
                  imageUrl={coverImage}
                />
              </div>

              {blog.summary ? <p className="lead-summary mb-4">{blog.summary}</p> : null}

              <div
                className="detail-block mb-5"
                dangerouslySetInnerHTML={{ __html: blog.description || "" }}
              />
            </div>
          </div>

          {features.length > 0 ? (
            <div className="blog-detail-popular">
              <hr className="blog-detail-popular__rule" />
              <div className="row mt-4">
                <div className="col-12 mb-3">
                  <h2 className="home-section-title">
                    Popular <span className="strong">Blogs</span>
                  </h2>
                </div>
                {features.map((feature) => (
                  <div className="col-md-4 mb-3" key={feature.id}>
                    {feature.external_url ? (
                      <a
                        href={feature.external_url}
                        className="featured-card text-decoration-none d-block"
                        target="_blank"
                        rel="noreferrer"
                      >
                        <div
                          className="blog-img"
                          style={{
                            backgroundImage: `url(${getBlogCardImage(feature)})`,
                          }}
                        />
                        <div className="blog-content">
                          <span className="creation-date">{feature.days}</span>
                          <h6 className="my-2">{feature.title}</h6>
                        </div>
                      </a>
                    ) : (
                      <Link
                        to={BLOG_DETAIL.replace(":slug", feature.slug)}
                        className="featured-card text-decoration-none d-block"
                      >
                        <div
                          className="blog-img"
                          style={{
                            backgroundImage: `url(${getBlogCardImage(feature)})`,
                          }}
                        />
                        <div className="blog-content">
                          <span className="creation-date">{feature.days}</span>
                          <h6 className="my-2">{feature.title}</h6>
                        </div>
                      </Link>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </Container>
      </section>

      <BlogStayInLoopCTA />
    </div>
  );
};

export default BlogDetail;
